/**
 * <product-form> custom element — shared by the product page and the quick-add drawer on
 * collection pages (moved out of sections/product.liquid on 2026-09-06: section {% javascript %}
 * is only injected for files in a page's render tree, so the class was missing on collections).
 * Loaded deferred from layout/theme.liquid.
 */
/**
 * Product Form - Handles variant selection and image updates
 *
 * Architecture:
 * - Custom element for encapsulation
 * - Event delegation for performance
 * - Variant-image linking via data attributes
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.priceElement = this.closest('[data-quick-add-content]')?.querySelector('[data-product-price]') || document.querySelector('[data-product-price]');
    this.addToCartButton = this.querySelector('[data-add-to-cart]');
    this.addToCartText = this.querySelector('[data-add-to-cart-text]');

    // Parse product JSON data
    const productJson = this.closest('[data-quick-add-content]')?.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
    this.productData = productJson ? JSON.parse(productJson.textContent) : null;

    // Media elements
    this.mainImage = document.querySelector('[data-main-image]');
    this.thumbnails = document.querySelectorAll('[data-thumbnail]');

    this.init();
  }

  init() {
    if (!this.form || !this.productData) return;

    // Listen for option changes
    this.form.addEventListener('change', this.onOptionChange.bind(this));

    // Thumbnail clicks
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', this.onThumbnailClick.bind(this));
    });

    // Quantity controls
    const quantityMinus = this.querySelector('[data-quantity-minus]');
    const quantityPlus = this.querySelector('[data-quantity-plus]');
    const quantityInput = this.querySelector('[data-quantity-input]');

    if (quantityMinus && quantityPlus && quantityInput) {
      quantityMinus.addEventListener('click', () => this.updateQuantity(-1));
      quantityPlus.addEventListener('click', () => this.updateQuantity(1));
    }

    // Form submission
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Only list option values that exist for the preselected earlier options
    this.refreshOptionValues();
  }

  /**
   * Handle option (color/size) change
   */
  onOptionChange(event) {
    const input = event.target;
    if (!input.matches('input[type="radio"]')) return;

    this.refreshOptionValues();
    this.syncSelectedLabels();
    this.updateVariant();
  }

  /**
   * Cascading option values (2026-09-23): Shopify keeps option values per
   * PRODUCT, so the server lists every colour even when the selected handle
   * length only comes in three of them — clicking such a colour used to do
   * nothing (bob-ross-beutel). Groups after the first now show only the values
   * that exist together with the values chosen in the groups before them;
   * a value that disappears while checked falls to the first listed one.
   * The Liquid templates render the same state for the initial variant.
   */
  refreshOptionValues() {
    const variants = this.productData.variants;
    const groups = [...this.form.querySelectorAll('fieldset')]
      .map(fieldset => [...fieldset.querySelectorAll('input[type="radio"][data-option-index]')])
      .filter(radios => radios.length > 0);
    const selected = [];
    groups.forEach((radios, index) => {
      let checked = radios.find(radio => radio.checked) || null;
      radios.forEach(radio => {
        const exists = index === 0 || variants.some(v =>
          v.options[index] === radio.value && selected.every((value, j) => v.options[j] === value)
        );
        const label = radio.closest('label');
        if (label) label.hidden = !exists;
        radio.disabled = !exists;
        if (!exists && radio.checked) {
          radio.checked = false;
          checked = null;
        }
      });
      if (!checked) {
        checked = radios.find(radio => !radio.disabled) || null;
        if (checked) checked.checked = true;
      }
      selected.push(checked ? checked.value : undefined);
    });
  }

  /**
   * Find and select variant based on current options
   */
  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    if (!variant) {
      this.updateAddToCartButton(false);
      return;
    }

    {
      // Update variant ID
      this.variantIdInput.value = variant.id;

      // Update price
      if (this.priceElement) {
        const formatter = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR'
        });
        this.priceElement.textContent = formatter.format(variant.price / 100);
      }

      // Update button state
      this.updateAddToCartButton(variant.available);

      // Update the image: the variant's own, or — when it has none (65 products
      // carry images only on some variants, e.g. the blanks' Beidseitig
      // variants, one colour of a pullover) — the image of the sibling variant
      // that shares the most option values, so a colour switch never leaves
      // the previous colour's photo on screen
      const image = variant.featured_image || this.fallbackImageFor(variant);
      if (image) {
        this.updateMainImage(image);

        // Also update thumbnail active state
        this.updateThumbnailState(image.id);
      }

      // Update URL without reload (not for the quick-add drawer on collection pages)
      if (!this.hasAttribute('data-no-url')) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
  }

  /**
   * Get currently selected option values
   */
  getSelectedOptions() {
    const options = [];
    this.form.querySelectorAll('input[type="radio"]:checked').forEach(input => {
      options.push(input.value);
    });
    return options;
  }

  /**
   * Find variant matching selected options
   */
  findVariant(selectedOptions) {
    return this.productData.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  /**
   * is-selected on the checked label of every group + the legend value text
   * (refreshOptionValues checks radios programmatically, which fires no
   * change event)
   */
  syncSelectedLabels() {
    this.form.querySelectorAll('fieldset').forEach(fieldset => {
      let selectedLabel = null;
      fieldset.querySelectorAll('label').forEach(label => {
        const checked = !!label.querySelector('input[type="radio"]:checked');
        label.classList.toggle('is-selected', checked);
        if (checked) selectedLabel = label;
      });
      const out = fieldset.querySelector('.pdp__legend-value');
      if (out && selectedLabel) {
        const span = selectedLabel.querySelector('span:last-child');
        const radio = selectedLabel.querySelector('input[type="radio"]');
        out.textContent = (span && span.textContent.trim()) || (radio ? radio.value : '');
      }
    });
  }

  /**
   * Image for a variant without its own: the sibling in the SAME COLOUR that
   * shares the most other option values (the blanks' Beidseitig variants →
   * the Einseitig photo of that colour). A colour without any photo returns
   * null — the image is left as it is rather than showing another colour.
   * Products without a colour option use the closest sibling.
   */
  fallbackImageFor(variant) {
    const options = this.productData.options || [];
    const colorIndex = options.findIndex(name => /^(farbe|color|colour)$/i.test(String(name)));
    let best = null;
    let bestScore = 0;
    for (const other of this.productData.variants) {
      if (!other.featured_image || other === variant) continue;
      if (colorIndex >= 0 && other.options[colorIndex] !== variant.options[colorIndex]) continue;
      const score = other.options.reduce((n, value, i) => n + (value === variant.options[i] ? 1 : 0), 0);
      if (score > bestScore) {
        bestScore = score;
        best = other.featured_image;
      }
    }
    return best;
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartButton(available) {
    if (this.addToCartButton && this.addToCartText) {
      this.addToCartButton.disabled = !available;
      const b = this.addToCartButton.dataset;
      this.addToCartText.textContent = available ? (b.labelAdd || 'In den Warenkorb') : (b.labelSoldOut || 'Nicht verfügbar');
    }
  }

  /**
   * Update main product image
   */
  updateMainImage(imageData) {
    if (!this.mainImage || !imageData) return;

    this.mainImage.classList.add('is-loading');
    this.mainImage.src = imageData.src;
    this.mainImage.srcset = imageData.srcset;

    this.mainImage.onload = () => {
      this.mainImage.classList.remove('is-loading');
    };
  }

  /**
   * Update thumbnail active state
   */
  updateThumbnailState(imageId) {
    this.thumbnails.forEach(thumbnail => {
      const isActive = thumbnail.dataset.imageId == imageId;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * Handle thumbnail click
   */
  onThumbnailClick(event) {
    const thumbnail = event.currentTarget;

    // Update main image
    if (this.mainImage) {
      this.mainImage.classList.add('is-loading');
      this.mainImage.src = thumbnail.dataset.imageSrc;
      this.mainImage.srcset = thumbnail.dataset.imageSrcset;

      this.mainImage.onload = () => {
        this.mainImage.classList.remove('is-loading');
      };
    }

    // Update active state
    this.thumbnails.forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('aria-current', 'false');
    });
    thumbnail.classList.add('is-active');
    thumbnail.setAttribute('aria-current', 'true');
  }

  /**
   * Update quantity
   */
  updateQuantity(delta) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;

    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 99;
    const currentValue = parseInt(input.value) || 1;
    const newValue = Math.min(Math.max(currentValue + delta, min), max);

    input.value = newValue;

    // Update button states
    const minusBtn = this.querySelector('[data-quantity-minus]');
    const plusBtn = this.querySelector('[data-quantity-plus]');

    if (minusBtn) minusBtn.disabled = newValue <= min;
    if (plusBtn) plusBtn.disabled = newValue >= max;
  }

  /**
   * Handle form submission
   */
  async onSubmit(event) {
    event.preventDefault();

    if (this.addToCartButton.disabled || this.submitting) return;

    // Loading state without disabling: keeps focus on the button so the drawer can return it (WCAG 2.4.3)
    this.submitting = true;
    this.addToCartButton.classList.add('is-loading');
    this.addToCartButton.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(this.form);
      // Section Rendering API: get the cart drawer back with the same request
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      // The cart drawer swaps its markup from data.sections and updates the header badge
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item: data, sections: data.sections }
      }));

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.addToCartText.textContent = error.message || 'Fehler - Erneut versuchen';
    } finally {
      this.submitting = false;
      this.addToCartButton.classList.remove('is-loading');
      this.addToCartButton.removeAttribute('aria-busy');

      // Reset button text after delay
      setTimeout(() => {
        if (this.addToCartText) {
          this.addToCartText.textContent = 'In den Warenkorb';
        }
      }, 2000);
    }
  }
}

// Register custom element
if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
